<!--

	Displays Vimeo videos in the embedded player providing an API for controlling it

	Note that while this player is mostly reactive, you should not assume that
	all props can be updated in all situations. In particular, you should make
	sure that it does not get recycled by different displays, e.g., by adding a
	:key to it in the parent component.

-->
<template>
	<div ref="vimeoContainer" class="mia-vimeoPlayerContainer">
		<div ref="overlayPopover" popover="manual" class="mia-vimeoPlayerOverlayPopover">
			<banner
				v-if="maxSpeedLimitEnabled"
				v-model:show="maxSpeedLimitWarningShown"
				class="mia-vimeoPlayerBanner"
				state="information"
				:mode="bannerMode"
				:icon-src="handWavingIcon"
				animate
				:auto-hide-duration-ms="3000"
			>
				<template #title>
					<span v-tr="{ key: 'vimeo_player_limit_message_title', speed: maxSpeedLimit ?? 1.25 }" />
				</template>
			</banner>
		</div>

		<div class="mia-vimeoPlayer">
			<iframe
				ref="vimeoPlayer"
				:src="videoSrc"
				:allowfullscreen="true"
				data-ready="true"
				allow="autoplay; encrypted-media"
				:class="isVerticalVideo ? 'mia-vertical' : 'mia-horizontal'"
			/>
			<slot />
		</div>

		<div class="mia-vimeoPlayerToast">
			<banner
				v-if="maxSpeedLimitEnabled"
				v-model:show="maxSpeedLimitWarningShown"
				class="mia-vimeoPlayerBanner"
				state="information"
				:mode="bannerMode"
				:icon-src="handWavingIcon"
				show-close-button
				animate
				:auto-hide-duration-ms="3000"
			>
				<template #title>
					<span v-tr="{ key: 'vimeo_player_limit_message_title', speed: maxSpeedLimit ?? 1.25 }" />
				</template>
			</banner>
		</div>
	</div>
</template>
<script lang="ts">
import { defineComponent, markRaw, type PropType } from "vue";
import type Player from "@vimeo/player";
import pDefer, { type DeferredPromise } from "p-defer";
import Log from "@/utils/Log";
import Banner, { type BannerMode } from "@/common/Banner.vue";
import HandWavingIcon from "@theme/icons/hand_waving.svg";

const VIMEO_READY_LOG_TIMEOUT_MS = 10000;

const VimeoPlayerComponent = defineComponent({
	name: "VimeoPlayer",
	components: {
		Banner,
	},
	props: {
		/** Vimeo embed URL for the iframe. */
		videoSrc: {
			type: String,
			required: true,
		},
		/** Fire a Google Analytics `play` event when playback starts. */
		enableGoogleTracking: {
			type: Boolean,
			default: false,
		},
		/** Start playback once the player is ready. */
		autoPlay: {
			type: Boolean,
			default: false,
		},
		/** Use the dark banner theme. */
		darkMode: {
			type: Boolean,
			default: false,
		},
		/** Target volume (0–1); null leaves Vimeo’s default. */
		volume: {
			type: [Number, null] as PropType<number | null>,
			default: null,
		},
		/** Text-track language to enable (e.g. `en`); null leaves tracks unchanged. */
		textTrack: {
			type: [String, null] as PropType<string | null>,
			default: null,
		},
		/** Seek target in seconds; null leaves the current time unchanged. */
		time: {
			type: [Number, null] as PropType<number | null>,
			default: null,
		},
		/** Imperative play/pause mirror for legacy callers. */
		isPlaying: {
			type: Boolean,
			default: false,
		},
		/** Use a portrait aspect ratio on the iframe. */
		isVerticalVideo: {
			type: Boolean,
			default: false,
		},
		/** Clamp playback rate and show the speed-limit banner when exceeded. */
		maxSpeedLimitEnabled: {
			type: Boolean,
			default: false,
		},
		/** Maximum allowed playback rate when limiting is enabled. */
		maxSpeedLimit: {
			type: [Number, null] as PropType<number | null>,
			default: null,
		},
		/** Forward player failures to `Log.error` (once per error name). */
		logVimeoPlayerErrors: {
			type: Boolean,
			default: false,
		},
	},
	emits: [
		"update:time",
		"update:volume",
		"pause",
		"play",
		"loaded",
		"ended",
	],
	data() {
		return {
			handWavingIcon: HandWavingIcon,
			player: undefined as unknown as DeferredPromise<Player>,
			/** Most recent time stamp (seconds) we reported via `update:time` event. */
			timeupdate: null as number | null,
			duration: 0,
			fullscreenHandler: null as (() => void) | null,
			loggedVimeoErrors: new Set<string>(),
			isUnmounted: false,
			vimeoReadyState: "pending" as "pending" | "ready" | "failed",
			readyTimeoutId: null as ReturnType<typeof setTimeout> | null,
			maxSpeedLimitWarningShown: false,
		};
	},
	computed: {
		bannerMode(): BannerMode {
			return this.darkMode ? "dark" : "light";
		},
	},
	watch: {
		time: "onTimeChange",
		volume: "onVolumeChange",
		textTrack: "onTextTrackChange",
		isPlaying: "onIsPlayingChange",
	},
	created() {
		this.player = pDefer<Player>();
		// Rejected before any listener attaches (e.g. unmount during load).
		this.player.promise.catch(() => { /* empty */ });
	},
	async mounted(): Promise<void> {
		try {
			this.fullscreenHandler = () => {
				const popover = this.$refs.overlayPopover;
				if (!(popover instanceof HTMLElement)) {
					return;
				}

				if (document.fullscreenElement) {
					try {
						popover.showPopover();
					}
					catch { /* empty */ }
				}
				else {
					try {
						popover.hidePopover();
					}
					catch { /* empty */ }
				}
			};
			document.addEventListener("fullscreenchange", this.fullscreenHandler);

			const iframe = this.$refs.vimeoPlayer;
			if (!(iframe instanceof HTMLIFrameElement)) {
				throw new Error("VimeoPlayer ref is not an HTMLIFrameElement.");
			}

			const Player = (await import("@vimeo/player")).default;

			if (this.isUnmounted) {
				return;
			}

			const player = markRaw(new Player(iframe));

			this.startReadyTimeout();
			await player.ready();
			this.vimeoReadyState = "ready";
			this.clearReadyTimeout();
			this.$emit("loaded");
			this.player.resolve(player);
			this.duration = await this.getDuration();

			await this.onTimeChange();
			await this.onVolumeChange();
			await this.onTextTrackChange();

			player.on("ended", () => this.$emit("ended"));
			player.on("pause", () => this.$emit("pause"));
			player.on("play", () => this.$emit("play"));
			player.on("timeupdate", (data) => {
				this.timeupdate = data.seconds;
				this.$emit("update:time", this.timeupdate, this.duration);
			});
			player.on("volumechange", (event) => {
				this.$emit("update:volume", event.volume);
			});
			player.on("error", (error) => {
				this.logVimeoErrorOnce(
					error.name,
					`Vimeo player error: [${error.name}] ${error.message} (method: ${error.method})`,
					undefined,
				);
			});

			if (this.autoPlay) {
				await this.play();
			}

			const maxSpeedLimit = this.maxSpeedLimit;
			if (this.maxSpeedLimitEnabled && maxSpeedLimit) {
				player.on("playbackratechange", (data) => {
					if (data.playbackRate > maxSpeedLimit) {
						this.maxSpeedLimitWarningShown = true;
						void player.setPlaybackRate(maxSpeedLimit);
					}
				});
			}
		}
		catch (error: unknown) {
			this.vimeoReadyState = "failed";
			this.clearReadyTimeout();
			this.$emit("loaded");
			this.player.reject(error);
			this.logVimeoErrorOnce(
				"PlayerLoadFailure",
				"Vimeo player failed to load",
				error,
			);
		}
	},
	beforeUnmount(): void {
		this.isUnmounted = true;
		this.clearReadyTimeout();
		if (this.vimeoReadyState === "pending") {
			this.logVimeoErrorOnce(
				"UnmountedBeforeReady",
				"Vimeo player was unmounted before it became ready",
				undefined,
			);
		}
		if (this.fullscreenHandler) {
			document.removeEventListener("fullscreenchange", this.fullscreenHandler);
		}
	},
	methods: {
		getErrorType(error: unknown, fallbackType: string): string {
			if (error instanceof Error && error.name) {
				return error.name;
			}
			return fallbackType;
		},
		logVimeoErrorOnce(errorName: string, baseMessage: string, error: unknown): void {
			if (!this.logVimeoPlayerErrors || this.loggedVimeoErrors.has(errorName)) {
				return;
			}

			let message = baseMessage;
			if (error !== undefined) {
				message += `: ${error instanceof Error ? error.message : String(error)}`;
			}
			this.loggedVimeoErrors.add(errorName);
			void Log.error(message);
		},
		startReadyTimeout(): void {
			this.readyTimeoutId = setTimeout(() => {
				if (this.vimeoReadyState === "pending") {
					this.logVimeoErrorOnce(
						"ReadyTimeout",
						`Vimeo player did not become ready within ${VIMEO_READY_LOG_TIMEOUT_MS} ms (the video may be slow to load or unable to connect)`,
						undefined,
					);
				}
			}, VIMEO_READY_LOG_TIMEOUT_MS);
		},
		clearReadyTimeout(): void {
			if (this.readyTimeoutId !== null) {
				clearTimeout(this.readyTimeoutId);
				this.readyTimeoutId = null;
			}
		},
		async getDuration(): Promise<number> {
			return (await this.player.promise).getDuration();
		},
		/**
		 * Implementation note: This is not a good pattern. Instead, there should be a
		 * reactive .syncable "state" prop that controls whether the player is
		 * currently paused or playing. However, we need this interface to support
		 * some legacy code paths that we did not want to risk breaking in the latest
		 * round of changes here.
		 */
		async play(): Promise<void> {
			try {
				await (await this.player.promise).play();

				if (this.enableGoogleTracking && typeof gtag === "function") {
					gtag("event", "play", {
						event_category: "video",
						event_label: this.videoSrc,
					});
				}
			}
			catch (error: unknown) {
				this.logVimeoErrorOnce(
					`play:${this.getErrorType(error, "PlayFailure")}`,
					"Vimeo player play() failed",
					error,
				);
			}
		},
		async pause(): Promise<void> {
			try {
				await (await this.player.promise).pause();
			}
			catch (error: unknown) {
				this.logVimeoErrorOnce(
					`pause:${this.getErrorType(error, "PauseFailure")}`,
					"Vimeo player pause() failed",
					error,
				);
			}
		},
		async unload(): Promise<void> {
			try {
				await (await this.player.promise).ready();
				await (await this.player.promise).unload();
			}
			catch (error: unknown) {
				this.logVimeoErrorOnce(
					`unload:${this.getErrorType(error, "UnloadFailure")}`,
					"Vimeo player unload() failed",
					error,
				);
			}
		},
		async onTimeChange(): Promise<void> {
			if (this.time == null) {
				return;
			}

			if (this.time !== this.timeupdate) {
				this.timeupdate = this.time;
				try {
					// We must not set the time to exactly 0, see
					// https://github.com/vimeo/player.js/issues/178 (which is closed but not
					// fixed as of early 2025.)
					await (await this.player.promise).setCurrentTime(Math.max(this.time, Number.EPSILON));
				}
				catch (error: unknown) {
					this.logVimeoErrorOnce(
						`setCurrentTime:${this.getErrorType(error, "SetCurrentTimeFailure")}`,
						"Vimeo player setCurrentTime() failed",
						error,
					);
				}
			}
		},
		async onVolumeChange(): Promise<void> {
			if (this.volume == null) {
				return;
			}

			try {
				await (await this.player.promise).setVolume(this.volume);
			}
			catch (error: unknown) {
				this.logVimeoErrorOnce(
					`setVolume:${this.getErrorType(error, "SetVolumeFailure")}`,
					"Vimeo player setVolume() failed",
					error,
				);
			}
		},
		async onTextTrackChange(): Promise<void> {
			try {
				const player = await this.player.promise;
				if (this.textTrack == null) {
					await player.disableTextTrack();
					return;
				}

				await player.enableTextTrack(this.textTrack);
			}
			catch (error: unknown) {
				const action = this.textTrack == null ? "disableTextTrack" : `enableTextTrack("${this.textTrack}")`;
				this.logVimeoErrorOnce(
					`${action}:${this.getErrorType(error, "TextTrackFailure")}`,
					`Vimeo player ${action} failed`,
					error,
				);
			}
		},
		async onIsPlayingChange(): Promise<void> {
			if (this.isPlaying) {
				await this.play();
				return;
			}
			await this.pause();
		},
		/**
		 * @deprecated This method exists to support some legacy code paths and should not be used anymore.
		 * Set the "time" directly instead.
		 */
		async setVideoProgress(seconds: number): Promise<void> {
			try {
				await (await this.player.promise).setCurrentTime(seconds);
			}
			catch (error: unknown) {
				this.logVimeoErrorOnce(
					`setVideoProgress:${this.getErrorType(error, "SetVideoProgressFailure")}`,
					`Vimeo player setVideoProgress(${seconds}) failed`,
					error,
				);
			}
		},
	},
});

export type VimeoPlayer = InstanceType<typeof VimeoPlayerComponent>;
export default VimeoPlayerComponent;
</script>
<style lang="scss">
.mia-vimeoPlayerContainer {
	position: relative;
	width: 100%;
	display: block;

	&:fullscreen {
		background: theme.$mia-color-neutral-1000;

		.mia-vimeoPlayer {
			height: 100%;
		}

		iframe {
			height: 100%;
		}
	}

	.mia-vimeoPlayer {
		position: relative;
		width: 100%;
		iframe {
			border: none;
			width: 100%;
			display: block;

			&.mia-vertical {
				aspect-ratio: 9 / 16;
			}

			&.mia-horizontal {
				aspect-ratio: 16 / 9;
			}
		}
	}

	.mia-vimeoPlayerBanner.mia-Banner {
		box-shadow: theme.$mia-shadow-box;
	}

	.mia-vimeoPlayerOverlayPopover {
		position: fixed;
		inset: auto;
		top: theme.$mia-margin-size;
		right: theme.$mia-margin-size;
		width: theme.$mia-vimeo-player-banner-width;
		max-width: calc(100% - #{theme.$mia-margin-size-large});
		margin: 0;
		border: none;
		padding: 0;
		background: transparent;
		overflow: hidden;
	}

	.mia-vimeoPlayerToast {
		position: absolute;
		top: theme.$mia-margin-size;
		right: theme.$mia-margin-size;
		width: theme.$mia-vimeo-player-banner-width;
		max-width: calc(100% - #{theme.$mia-margin-size-large});
		overflow: hidden;
		pointer-events: none;

		> * {
			pointer-events: auto;
		}
	}
}
</style>
